import { useConfigStore, useCategoryTypeStore } from '@/stores';
import styles from './styles.module.scss';
import ShopFnbFacilityCategoryButton from '@/components/common/shop-fnb-facility/buttons/category';
import { useCategoryTypeCategories } from '@/hooks/poi/useCategoryTypeData';
import type { BuildingCategory } from '@/services/dto';
import { useTranslation } from 'react-i18next';
import { CATEGORY_TYPE_SEARCH_MODE } from '@/enums/category-type';
import { useOrientation } from '@/hooks';
import { getCategoryLocalizedName } from '@/utils/poi';
import clsx from 'clsx';

const getCategoryLabel = (category: BuildingCategory, language: string, t: (key: string) => string): string => {
    if (category.clientCode === 'all') {
        const name = category.name;
        if (typeof name === 'string') return t(name);
        if (Array.isArray(name)) return getCategoryLocalizedName(category, language);
        return t(name[language]);
    }
    return getCategoryLocalizedName(category, language);
};

interface CategoryTypeSearchByCategoryProps {
    menuId?: string;
    onSelectCategory?: (category: BuildingCategory) => void;
}

const CategoryTypeSearchByCategory = ({ menuId, onSelectCategory }: CategoryTypeSearchByCategoryProps) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const { t } = useTranslation();
    const selectedCategory = useCategoryTypeStore((state) => state.selectedCategory);
    const setSelectedCategory = useCategoryTypeStore((state) => state.setSelectedCategory);
    const updateCategoryTypeContents = useCategoryTypeStore((state) => state.updateCategoryTypeContents);
    const categories = useCategoryTypeCategories(menuId);

    const handleSelectCategory = (category: BuildingCategory) => {
        setSelectedCategory(category);
        onSelectCategory?.(category);
        updateCategoryTypeContents(CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_CATEGORY);
    };

    return (
        <div
            data-region-name="category-type-search-by-category"
            className={clsx(styles['category-type-search-by-category'], {
                [styles['category-type-search-by-category--vertical']]: !isHorizontal,
                [styles['category-type-search-by-category--vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            data-region-nav
        >
            {categories.map((category) => (
                <ShopFnbFacilityCategoryButton
                    tabIndex={9}
                    data-interactive
                    role="radio"
                    type="button"
                    aria-checked={selectedCategory?.clientCode === category.clientCode}
                    aria-label={getCategoryLabel(category, language, t)}
                    key={category.clientCode}
                    onClick={() => handleSelectCategory(category)}
                    title={getCategoryLabel(category, language, t)}
                />
            ))}
        </div>
    );
};

export default CategoryTypeSearchByCategory;
